import React, { useMemo, useRef, useEffect } from 'react'
import { useDataChange } from 'Simple/Data.js'
import { Editor as TinyMceEditor } from '@tinymce/tinymce-react'
import './style.css'

let TOOLBAR_ARRANGEMENTS = {
  default:
    'insertfile undo redo | fontselect styleselect | bold italic | fontsizeselect forecolor backcolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | link',
  advanced:
    'insertfile undo redo | fontselect styleselect | bold italic | fontsizeselect forecolor backcolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | linkAsButton customImageDialog',
  note_template:
    'bold italic underline bullist numlist forecolor backcolor link',
  simplified:
    'bold italic underline bullist numlist forecolor backcolor linkAsButton',
  sms_template: ' ',
}

/**
 * @param {any} item
 * @param {import('./types.d.ts').Editor} editor
 * @returns
 */
function getLastCharBeforeCursor(editor) {
  let rng = editor.selection.getRng()
  let container = rng.startContainer
  let offset = rng.startOffset

  if (container.nodeType === Node.TEXT_NODE) {
    let textBefore = container.textContent.slice(0, offset)
    return textBefore.length > 0 ? textBefore.slice(-1) : null
  }

  if (container.nodeType === Node.ELEMENT_NODE) {
    let child = container.childNodes[offset - 1]
    if (child && child.nodeType === Node.TEXT_NODE) {
      return child.textContent.slice(-1)
    }
  }

  return null
}

/**
 * @param {any} item
 * @param {import('./types.d.ts').Editor} editor
 * @returns
 */
function generateMenuItem(item, editor) {
  return {
    type: item.type,
    text: item.text,
    onAction: () => {
      // Insert content at the location of the cursor.
      // https://www.tiny.cloud/docs/api/tinymce/tinymce.editor/#insertcontent
      // check if it contains space before the current token, if it doesnt add space + token
      let lastDigitBeforeCursor = getLastCharBeforeCursor(editor)
      editor.insertContent(
        `${lastDigitBeforeCursor && lastDigitBeforeCursor !== ' ' ? ' ' : ''}${item.value}`
      )
    },
  }
}

export default function EditableTextArea(props) {
  let setEditor = useDataChange({
    context: 'editor',
    viewPath: props.viewPath,
  })

  let initConfig = useMemo(() => {
    /** @type {import('./types.d.ts').EditorInitConfig} */
    let initConfig = {
      // core wrongly outputs <span><li>... using spain instead of ul or ol, this lets Tiny allow it
      // https://www.tiny.cloud/docs/configure/content-filtering/#valid_children
      valid_children: '+body[style],+span[li],+div[li]',
      plugins: [
        'advlist',
        'autolink',
        'image',
        'lists',
        'link',
        'charmap',
        'preview',
        'searchreplace',
        'visualblocks',
        'code',
        'fullscreen',
        'insertdatetime',
        'table',
      ],
      toolbar:
        typeof props.toolbar === 'boolean'
          ? props.toolbar
          : TOOLBAR_ARRANGEMENTS[props.toolbar] || TOOLBAR_ARRANGEMENTS.default,
      menubar:
        typeof props.menubar === 'boolean'
          ? props.menubar
          : 'edit insert view format table tools',
      contextmenu: false,
      content_style:
        "@import url('https://fonts.googleapis.com/css?family=Calibri:400,700,400italic,700italic')",
      font_formats:
        'Arial=arial,helvetica,sans-serif; Calibri=calibri; Georgia=georgia,palatino; Helvetica=helvetica; Times New Roman=times new roman,times',
      width: '100%',
      height: props.height || '100%',
      init_instance_callback: editor => {
        // After editor is initialized set the true content
        editor.resetContent(props.value)
      },
      statusbar: false,
      skin_url: `${window.location.origin}/tinymce-skin`,
      selector: props.selector ? `.${props.selector}` : null,
      inline: props.inline || false,
      fixed_toolbar_container: props.inlineToolbarContainer
        ? `#${props.inlineToolbarContainer}`
        : null,
      toolbar_persist: props.toolbarPersist || false,
      browser_spellcheck: true,
      text_patterns: [],
      link_title: false,
      link_target_list: false,
      link_default_target: '_blank',
      placeholder: props.placeholder || undefined,
      ...(props.plaintext
        ? {
            plugins: [],
            paste_as_text: true,
          }
        : {}),
    }

    if (props.tokens) {
      let { name, label, tooltip, items: menuItems } = props.tokens
      initConfig.toolbar = initConfig.toolbar.concat(` | ${name} `)
      initConfig.setup = editor => {
        // Register a custom toolbar menu button to insert tokens
        // https://www.tiny.cloud/docs/ui-components/typesoftoolbarbuttons/#menubutton
        editor.ui.registry.addMenuButton(name, {
          text: label,
          tooltip,
          fetch: callback => {
            let items = menuItems.map(item => {
              if (item.type === 'nestedmenuitem') {
                return {
                  type: item.type,
                  text: item.text,
                  getSubmenuItems: () =>
                    item?.submenu_items?.map(submenuItem =>
                      generateMenuItem(submenuItem, editor)
                    ) ?? [],
                }
              } else {
                return generateMenuItem(item, editor)
              }
            })
            callback(items)
          },
        })
      }
    }

    if (props.autocomplete) {
      initConfig.setup = editor => {
        editor.ui.registry.addAutocompleter('tinymce-autocompleter', {
          trigger: '/',
          minChars: 0,
          columns: 1,
          onAction: (autocompleteApi, rng, value) => {
            editor.selection.setRng(rng)
            props.onAutocompleteAction(value)
            autocompleteApi.hide()
          },
          fetch: props.onAutocompleteFetch,
        })
      }
    }

    return initConfig
  }, [
    props.value,
    props.tokens,
    props.menubar,
    props.toolbar,
    props.autocomplete,
    props.selector,
    props.inline,
    props.inlineToolbarContainer,
    props.toolbarPersist,
    props.onAutocompleteAction,
    props.onAutocompleteFetch,
    props.placeholder,
    props.plaintext,
    props.height,
  ])

  let onChangeRef = useRef(props.onChange)
  useEffect(() => {
    onChangeRef.current = props.onChange
  }, [props.onChange])

  return (
    <TinyMceEditor
      tinymceScriptSrc={`${window.location.origin}/tinymce/tinymce.min.js`}
      apiKey={process.env.REACT_APP_TINY_MCE_API_KEY}
      onInit={handleOnInit}
      disabled={props.disabled}
      onDirty={() =>
        setEditor(next => {
          next.isDirty = true
        })
      }
      onEditorChange={(value, editor) => {
        if (typeof onChangeRef.current === 'function') {
          if (props.plaintext === true) {
            onChangeRef.current(getPlaintextValue(editor))
          } else {
            onChangeRef.current(value)
          }
        }
      }}
      initialValue="" /* Initialize Editor Empty to prevent the base64 image issue */
      init={initConfig}
    />
  )

  /**
   * @param {import('./types.d.ts').Editor} editor
   * @returns {string}
   */
  function getPlaintextValue(editor) {
    return editor.getContent({ format: 'text' })
  }

  /**
   * @param {import('./types.d.ts').Editor} editor
   * @returns {void}
   */
  function addCharacterLimiter(editor) {
    editor.on('keydown', event => {
      // FIXME: this thing needs some love
      // if (getPlaintextValue(editor).length >= props.maxLength) {
      //   event.preventDefault();
      //   event.stopPropagation()
      // }
    })
  }

  /**
   * @param {import('./types.d.ts').Editor} editor
   * @returns {void}
   */
  function disableShortcuts(editor) {
    Object.keys(editor.shortcuts.shortcuts).forEach(shortcut => {
      editor.shortcuts.remove(shortcut.replace(',', '+'))
    })
  }

  /**
   * @param {unknown} evt
   * @param {import('./types.d.ts').Editor} editor
   */
  function handleOnInit(evt, editor) {
    setEditor(next => {
      next.api = editor
      next.isInitialized = true
    })

    if (props.plaintext) {
      disableShortcuts(editor)
    }

    if (props.maxLength) {
      addCharacterLimiter(editor)
    }

    if (props.autoFocus) {
      editor.focus()
    }

    // Add paste handler to ensure onChange is triggered on paste
    editor.on('paste', () => {
      // Use setTimeout to ensure TinyMCE has processed the pasted content
      setTimeout(() => {
        if (typeof onChangeRef.current === 'function') {
          if (props.plaintext === true) {
            onChangeRef.current(getPlaintextValue(editor))
          } else {
            onChangeRef.current(editor.getContent())
          }
        }
      }, 0)
    })

    let getCurrentLinkData = () => {
      let node = editor.selection.getNode()
      let data = {
        href: '',
        text: editor.selection.getContent({ format: 'text' }) || '',
        makeButton: false,
      }
      if (node && node.nodeName === 'A') {
        data.href = node.getAttribute('href') || ''
        data.text = node.textContent || data.text
        // detect if it already looks like a button (by class)
        data.makeButton = (node.className || '').split(/\s+/).includes('btn')
      }
      return data
    }

    editor.ui.registry.addButton('customImageDialog', {
      icon: 'image',
      tooltip: 'Insert image by URL',
      onAction: function () {
        editor.windowManager.open({
          title: 'Insert image',
          body: {
            type: 'panel',
            items: [
              {
                type: 'htmlpanel',
                html: `
                  <p>Enter a direct image link (e.g., https://example.com/picture.jpg)</p>
                `,
              },
              {
                type: 'input',
                name: 'src',
                label: 'Source',
              },
              {
                type: 'input',
                name: 'alt',
                label: 'Alt text',
              },
              {
                type: 'htmlpanel',
                html: `
                  <p>Leave these blank if you want to insert image in original size</p>
                `,
              },
              {
                type: 'input',
                name: 'width',
                label: 'Width (px)',
              },
              {
                type: 'input',
                name: 'height',
                label: 'Height (px)',
              },
            ],
          },
          buttons: [
            { type: 'cancel', text: 'Cancel' },
            { type: 'submit', text: 'Insert', primary: true },
          ],
          onSubmit: function (api) {
            let data = api.getData()
            let width = data.width ? `width="${data.width}"` : ''
            let height = data.height ? `height="${data.height}"` : ''

            if (data.src) {
              editor.insertContent(`
                <img src="${data.src}" alt="${data.alt || ''}" ${width} ${height} style="max-width:100%;" />
              `)
            }
            api.close()
          },
        })
      },
    })

    editor.ui.registry.addButton('linkAsButton', {
      text: '',
      tooltip: 'Insert/edit link',
      icon: 'link',
      onAction: () => {
        let current = getCurrentLinkData()
        editor.windowManager.open({
          title: 'Insert Link / Button',
          body: {
            type: 'panel',
            items: [
              { type: 'input', name: 'href', label: 'Website address' },
              { type: 'input', name: 'text', label: 'Text to display' },
              {
                type: 'checkbox',
                name: 'makeButton',
                label: 'Make this link a button',
              },
            ],
          },
          initialData: {
            href: current.href,
            text: current.text,
            makeButton: current.makeButton,
          },
          buttons: [
            { type: 'cancel', text: 'Cancel' },
            { type: 'submit', text: 'Insert', primary: true },
          ],
          onSubmit: api => {
            let data = api.getData()
            let href = editor.dom.encode(data.href || '#')
            let text = editor.dom.encode(data.text || data.href || 'link')

            let styles = [
              'display:inline-block',
              'padding:4px',
              'border-radius:4px',
              'background:#2F8132',
              'color:#fff',
              'text-decoration:none',
              'font-weight:500',
            ].join(';')

            let html
            if (data.makeButton) {
              html = `<a href="${href}" class="btn" target="_blank" rel="noopener noreferrer" title=${text} style=${styles}>${text}</a>`
            } else {
              html = `<a href="${href}" target="_blank" rel="noopener noreferrer" title=${text}>${text}</a>`
            }

            editor.insertContent(html)

            api.close()
          },
        })
      },
    })
  }
}
